/**
 * FeatureGuard Component
 *
 * Route guard component that checks if a feature flag is enabled.
 * If disabled, redirects to /not-found following established project patterns.
 * If enabled, renders the children as normal.
 */

import { authRoutes } from '@auth/routes';
import type { FeatureFlag } from '@core/domain';
import { useIsFeatureEnabled } from '@core/hooks';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface FeatureGuardProps {
  /** The feature flag to check */
  featureFlag: FeatureFlag;
  /** Children to render if feature is enabled */
  children: ReactNode;
}

/**
 * Route guard for feature-flagged pages
 * Prevents access to disabled features via direct URL navigation
 *
 * Follows the established project pattern of redirecting to /not-found
 * when access is denied, consistent with AuthenticatedRoute and CommunityAuditGuard.
 * A disabled feature flag is semantically equivalent to "this route doesn't exist for you".
 */
export function FeatureGuard({ featureFlag, children }: FeatureGuardProps) {
  const isFeatureEnabled = useIsFeatureEnabled(featureFlag);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isFeatureEnabled) {
      navigate(authRoutes.notFound);
    }
  }, [isFeatureEnabled, navigate]);

  if (!isFeatureEnabled) return null;

  return children;
}
